import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    CORRECTION_STATES,
    NOT_APPLICABLE_STYLE,
    PACING_STATES,
} from '../../utils/spendPacing';

const PILL_BASE_CLASSES =
    'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full whitespace-nowrap text-gray-900';

/**
 * Pill for a spend pacing state. `kind` picks the palette:
 * - 'state': cumulative/pace state (behind, on_track, overspending)
 * - 'correction': correction state (correcting, diverging, on_track); null reads "sin datos"
 * - 'na': campaign not evaluated (not scheduler_mode 1)
 * `size` is 'xs' (default) or 'sm'.
 */
function PacingPill({ kind = 'state', state = null, title, size = 'xs' }) {
    const { t } = useTranslation();
    const PILL_CLASSES = `${PILL_BASE_CLASSES} ${
        size === 'sm' ? 'text-sm' : 'text-xs'
    }`;
    const focusProps = title ? { title, tabIndex: 0 } : {};

    if (kind === 'na') {
        return (
            <span
                className={`${PILL_CLASSES} border border-dashed border-gray-300 text-gray-500`}
                style={{ backgroundColor: NOT_APPLICABLE_STYLE.background }}
                data-testid="pacing-pill"
                data-pacing-kind="na"
                {...focusProps}
            >
                {t(NOT_APPLICABLE_STYLE.labelKey)}
            </span>
        );
    }

    const palette = kind === 'correction' ? CORRECTION_STATES : PACING_STATES;
    const meta = palette[state];

    if (!meta) {
        return (
            <span
                className={`${PILL_CLASSES} border border-dashed border-gray-300 text-gray-500`}
                data-testid="pacing-pill"
                data-pacing-kind={kind}
                {...focusProps}
            >
                {t('sections.spend-pacing.correction.none')}
            </span>
        );
    }

    return (
        <span
            className={PILL_CLASSES}
            style={{ backgroundColor: meta.background }}
            data-testid="pacing-pill"
            data-pacing-kind={kind}
            data-pacing-state={state}
            {...focusProps}
        >
            <i
                aria-hidden="true"
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: meta.dot }}
                data-testid="pacing-pill-dot"
            />
            {t(meta.labelKey)}
        </span>
    );
}

export default PacingPill;
